import { Button, Card, CardActions, CardContent, Grid, TextField } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { PreAlertItem, preAlertValidation } from "./PreAlertItem";

import { CurrencySelector } from "domains/legacy-integration/components";
import { HighlightOff } from "@mui/icons-material";
import { ManagedProductStatus } from "domains/allocations/components";
import { ProductSelector } from "domains/products/components";
import localisations from "../../../localisation";

export type PreAlertItemProps = {
    index: number;
    fieldCompanyId: number;
    initialItem: PreAlertItem;
    onChange: (index: number, item: PreAlertItem, isValid: boolean) => void;
    onDelete?: (itemIndex: number) => void;
    nssMaterialNumberRequired: boolean;
};

export const PreAlertItemComponent: FC<PreAlertItemProps> = ({
    index,
    fieldCompanyId,
    initialItem: initialItemModel,
    onChange,
    onDelete,
    nssMaterialNumberRequired,
}) => {
    const { quantityLabel, unitValueLabel, materialNumberLabel } = localisations.createPreAlert.stages.selectItems;

    const [preAlertItem, setPreAlertItem] = useState<PreAlertItem>(initialItemModel);
    const [showMaterialNumberError, setShowMaterialNumberError] = useState<boolean>(false);
    const [showQuantityError, setShowQuantityError] = useState<boolean>(false);
    const [showCurrencyCodeyError, setShowCurrencyCodeError] = useState<boolean>(false);
    const [status, setStatus] = useState<boolean>(false);
    const [refresh, setRefresh] = useState(true);

    useEffect(() => {
        var isValid = preAlertValidation.preAlertItemIsValid(preAlertItem, nssMaterialNumberRequired);
        onChange(index, preAlertItem, isValid && status);
    }, [onChange, index, preAlertItem, status]);

    const safeParseInt = (value: string): number | null => {
        return value === null || isNaN(parseInt(value, 10)) ? null : parseInt(value, 10);
    };

    const safeParseFloat = (value: string): number | null => {
        return value === null || isNaN(parseFloat(value)) ? null : parseFloat(value);
    };

    const onStatusLoaded = useCallback((status: boolean): void => {
        setRefresh(false);
        setStatus(status);
    }, []);

    return (
        <Card key={"pa-item-" + index.toString()} style={{ margin: 8 }}>
            <CardContent>
                <Grid container>
                    <Grid item xs={12}>
                        <ProductSelector
                            id={`pre-alert-item-${index}-product-selector-field`}
                            fieldCompanyId={fieldCompanyId}
                            initialProductId={preAlertItem.productId ?? undefined}
                            onSelected={useCallback(
                                (productDetails) =>
                                    setPreAlertItem((prevState) => ({
                                        ...prevState,
                                        productId: productDetails?.id ?? 0,
                                        productSku: productDetails?.sku ?? "",
                                    })),
                                []
                            )}
                            required={true}
                        />
                        <ManagedProductStatus
                            productId={preAlertItem.productId}
                            onLoaded={onStatusLoaded}
                            forceRefresh={refresh}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id={`pre-alert-item-${index}-materialNumber-field`}
                            required={nssMaterialNumberRequired}
                            autoComplete="off"
                            error={
                                !preAlertValidation.materialNumberIsValid(
                                    preAlertItem.materialNumber,
                                    nssMaterialNumberRequired
                                ) && showMaterialNumberError
                            }
                            onBlur={() => setShowMaterialNumberError(true)}
                            label={materialNumberLabel}
                            fullWidth
                            margin="normal"
                            value={preAlertItem.materialNumber}
                            onChange={(event) => {
                                setPreAlertItem((prevState) => ({
                                    ...prevState,
                                    materialNumber: event.target.value,
                                }));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id={`pre-alert-item-${index}-quantity-field`}
                            required
                            error={!preAlertValidation.quantityIsValid(preAlertItem.quantity) && showQuantityError}
                            onBlur={() => {
                                setShowQuantityError(true);
                            }}
                            label={quantityLabel}
                            type="number"
                            fullWidth
                            InputProps={{ inputProps: { min: "1", max: "99999999", step: "1" } }}
                            margin="normal"
                            value={!preAlertItem.quantity ? "" : preAlertItem.quantity}
                            onChange={(event) => {
                                setPreAlertItem((prevState) => ({
                                    ...prevState,
                                    quantity: safeParseInt(event.target.value) ?? 0,
                                }));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CurrencySelector
                            key={`pre-alert-item-${index}-currency-field`}
                            selectedCurrencyCode={preAlertItem.currencyCode}
                            onSelected={useCallback(
                                (currencyCode) =>
                                    setPreAlertItem((prevState) => ({
                                        ...prevState,
                                        currencyCode: currencyCode,
                                    })),
                                []
                            )}
                            required={true}
                            error={
                                !preAlertValidation.currencyCodeIsValid(preAlertItem.currencyCode) &&
                                showCurrencyCodeyError
                            }
                            onBlur={() => setShowCurrencyCodeError(true)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id={`pre-alert-item-${index}-unitvalue-field`}
                            label={unitValueLabel}
                            fullWidth
                            type="number"
                            margin="normal"
                            value={!preAlertItem.unitValue ? "" : preAlertItem.unitValue}
                            onChange={(event) =>
                                setPreAlertItem((prevState) => ({
                                    ...prevState,
                                    unitValue: safeParseFloat(event.target.value),
                                }))
                            }
                        />
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions>
                <Button
                    id={`pre-alert-item-${index}-delete-button`}
                    onClick={() => {
                        if (onDelete) onDelete(index);
                    }}
                    color="secondary"
                    disabled={!onDelete}
                    variant="contained"
                >
                    <HighlightOff />
                </Button>
            </CardActions>
        </Card>
    );
};
